// Free-gift pop-up for new visitors.
// - Opens once, after 30s on the site. The clock runs across pages (sessionStorage), so browsing doesn't reset it.
// - Never interrupts: waits while the cart, the case offer, the menu or the homepage greeting is showing.
// - Fades in and out like the case offer (data-state on the dialog, styled in styles.css), steps glide via --next/--back.
// - Remembers dismissal, and reopens on the outcome step after the form posts.
const FADE_MS = 280;
const RETRY_MS = 4000;
const PENDING_KEY = 'mab-gift-pending';

class SLeadPopup extends HTMLElement {
  #controller = null;
  #timer = 0;
  #closing = 0;
  #key = 'lead-popup';
  #reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.#key = this.dataset.storageKey || 'lead-popup';
    const delay = Number(this.dataset.delay) || 30000;

    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#go(this.#current() + 1);
      if (event.command === '--back') this.#go(this.#current() - 1);
      if (event.command === '--dismiss') this.dismiss();
    }, { signal });

    this.dialog.addEventListener('change', (event) => {
      if (event.target.matches('[data-gift]')) window.setTimeout(() => this.#go(3), 180);
    }, { signal });

    // Capture phase runs before Shopify's spam check, so a signup is always noted,
    // even when the check reloads the page to post the form itself.
    this.dialog.addEventListener('submit', (event) => {
      if (event.target?.id !== 'lead-popup-form') return;
      try { sessionStorage.setItem(PENDING_KEY, String(Date.now())); } catch (e) { /* storage unavailable */ }
      this.#remember();
    }, { signal, capture: true });

    // Without the spam check, post in place. With it, let Shopify finish and reopen on success after the reload.
    this.dialog.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'lead-popup-form') return;
      if (event.defaultPrevented) {
        const submit = form.querySelector('[type="submit"]');
        if (submit && this.dataset.sending) submit.textContent = this.dataset.sending;
        return;
      }
      event.preventDefault();
      if (form.getAttribute('aria-busy') === 'true') return;
      this.#submit(form);
    }, { signal });

    // Esc and backdrop clicks use the same soft fade.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dismiss();
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dismiss();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.dialog.dataset.state = 'closed';
      this.#remember();
    }, { signal });

    // After a reload from a submit, reopen on the outcome step.
    const pending = this.#takePending();
    if (this.dialog.querySelector('[data-lead-success]')) return this.#open();
    if (this.dialog.querySelector('[data-lead-error]')) {
      this.#open();
      return this.#go(3);
    }
    if (pending && window.location.hash === '#lead-popup-form') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
      this.#open();
      return this.#go(4);
    }

    if (new URLSearchParams(window.location.search).has('gift-popup')) return this.#open();
    if (this.dataset.designMode === 'true') return;

    let seen = false;
    try { seen = localStorage.getItem(this.#key) === 'seen'; } catch (e) { seen = false; }
    if (seen) return;

    this.#schedule(Math.max(0, delay - this.#timeOnSite()));
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#closing);
  }

  dismiss() {
    if (!this.dialog.open || this.dialog.dataset.state === 'closing') return;
    this.dialog.dataset.state = 'closing';
    this.#remember();
    window.clearTimeout(this.#closing);
    this.#closing = window.setTimeout(() => {
      if (this.dialog.open) this.dialog.close();
    }, this.#reduce.matches ? 0 : FADE_MS);
  }

  // Milliseconds since this visit started, carried across page loads.
  #timeOnSite() {
    try {
      const now = Date.now();
      const start = Number(sessionStorage.getItem('mab-site-start')) || now;
      sessionStorage.setItem('mab-site-start', String(start));
      return now - start;
    } catch (e) {
      return 0;
    }
  }

  #schedule(ms) {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      const busy = document.querySelector('dialog[open]') || document.documentElement.classList.contains('is-intro');
      if (busy) return this.#schedule(RETRY_MS);
      this.#open();
    }, ms);
  }

  #open() {
    if (this.dialog.open) return;
    this.dialog.dataset.state = 'opening';
    // Keep initial focus on the dialog, not a heading with a visible browser focus ring.
    // Step headings still receive focus after navigation for screen-reader context.
    this.dialog.setAttribute('tabindex', '-1');
    this.dialog.setAttribute('autofocus', '');
    this.dialog.showModal();
    this.dialog.focus({ preventScroll: true });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.dialog.dataset.state === 'opening') this.dialog.dataset.state = 'open';
    }));
  }

  async #submit(form) {
    const body = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (typeof value === 'string') body.append(key, value);
    });
    const status = form.querySelector('[data-lead-status]');
    const submit = form.querySelector('[type="submit"]');
    const originalLabel = submit?.textContent;
    const controls = [...form.querySelectorAll('input, button')];
    const disabled = controls.map((control) => control.disabled);
    form.setAttribute('aria-busy', 'true');
    controls.forEach((control) => { control.disabled = true; });
    if (status) status.textContent = '';
    if (submit) submit.textContent = this.dataset.sending;
    const controller = new AbortController();
    const cancel = () => controller.abort();
    this.#controller.signal.addEventListener('abort', cancel, { once: true });
    const timeout = window.setTimeout(cancel, 30000);
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        signal: controller.signal,
      });
      const html = await response.text();
      const page = new DOMParser().parseFromString(html, 'text/html');
      const returnedForm = page.querySelector('#lead-popup-form');
      const success = returnedForm?.querySelector('[data-lead-success]');
      // A successful HTTP response alone is not proof that Shopify accepted the signup.
      if (response.ok && success) {
        try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* storage unavailable */ }
        this.#remember();
        this.#go(4);
        return;
      }
      // Shopify asked for a spam check after all: hand over to a normal post (reopens on success after reload).
      if (new URL(response.url, window.location.href).pathname.includes('/challenge')) {
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      const error = returnedForm?.querySelector('[data-lead-error]');
      if (status) status.textContent = error?.textContent.trim() || this.dataset.submitError;
    } catch (error) {
      if (this.isConnected && status) status.textContent = this.dataset.submitError;
    } finally {
      window.clearTimeout(timeout);
      this.#controller.signal.removeEventListener('abort', cancel);
      form.removeAttribute('aria-busy');
      controls.forEach((control, index) => { control.disabled = disabled[index]; });
      if (submit) submit.textContent = originalLabel;
    }
  }

  // True once, if this visitor submitted the form in the last 10 minutes.
  #takePending() {
    try {
      const at = Number(sessionStorage.getItem(PENDING_KEY));
      sessionStorage.removeItem(PENDING_KEY);
      return at > 0 && Date.now() - at < 600000;
    } catch (e) {
      return false;
    }
  }

  #remember() {
    try { localStorage.setItem(this.#key, 'seen'); } catch (e) { /* storage unavailable */ }
  }

  #current() {
    const visible = this.dialog.querySelector('[data-step]:not([hidden])');
    return visible ? Number(visible.dataset.step) : 1;
  }

  #go(step) {
    const steps = [...this.dialog.querySelectorAll('[data-step]')];
    const target = steps.find((el) => Number(el.dataset.step) === step);
    if (!target || !target.hidden) return;
    steps.forEach((el) => { el.hidden = el !== target; });
    // The previous step's controls left the view, so focus moves into the new step.
    const focusable = target.querySelector('[data-step-focus]') || target.querySelector('h2');
    focusable?.focus({ preventScroll: true });
  }
}

if (!customElements.get('s-lead-popup')) customElements.define('s-lead-popup', SLeadPopup);
